import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Product } from '@/types/product';
import { 
  createCart, 
  addToCart as shopifyAddToCart, 
  updateCartItem, 
  removeFromCart, 
  getCart,
  mockShopifyResponses,
  isShopifyConfigured,
  CartItem 
} from '@/lib/shopify';

// Cart state interface
interface CartState {
  cartId: string | null;
  items: CartItem[];
  isOpen: boolean;
  isLoading: boolean;
  subtotal: number;
  total: number;
  checkoutUrl: string;
  itemCount: number;
}

// Action types
type CartAction =
  | { type: 'SET_CART_ID'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CART_OPEN'; payload: boolean }
  | { type: 'SET_CART_DATA'; payload: any }
  | { type: 'ADD_ITEM_SUCCESS'; payload: any }
  | { type: 'UPDATE_ITEM_SUCCESS'; payload: any }
  | { type: 'REMOVE_ITEM_SUCCESS'; payload: any }
  | { type: 'CART_ERROR'; payload: string };

// Initial state
const initialState: CartState = {
  cartId: null,
  items: [],
  isOpen: false,
  isLoading: false,
  subtotal: 0,
  total: 0,
  checkoutUrl: '',
  itemCount: 0,
};

// Cart reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_CART_ID':
      return { ...state, cartId: action.payload };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload };
    
    case 'SET_CART_DATA':
    case 'ADD_ITEM_SUCCESS':
    case 'UPDATE_ITEM_SUCCESS':
    case 'REMOVE_ITEM_SUCCESS':
      const cart = action.payload;
      const lineItems = cart.lineItems || [];
      const mappedItems = lineItems.map((item: any) => ({
        id: item.id,
        productId: item.variant.product?.id || '',
        variantId: item.variant.id,
        title: item.title,
        quantity: item.quantity,
        price: parseFloat(item.variant.price?.amount || item.variant.price) || 35,
        image: item.variant.image?.src,
        size: item.variant.size || '',
        color: item.variant.color || item.variant.title || '',
      }));
      
      // Calculate subtotal manually to ensure accuracy
      const calculatedSubtotal = mappedItems.reduce((total, item) => total + (item.price * item.quantity), 0);
      
      return {
        ...state,
        isLoading: false,
        items: mappedItems,
        subtotal: calculatedSubtotal,
        total: calculatedSubtotal, // For now, total equals subtotal (before taxes/shipping)
        checkoutUrl: cart.webUrl || '',
        itemCount: lineItems.reduce((total: number, item: any) => total + item.quantity, 0),
      };
    
    case 'CART_ERROR':
      return { ...state, isLoading: false };
    
    default:
      return state;
  }
};

// Context interface
interface CartContextType {
  state: CartState;
  addToCart: (product: Product, variant: any, quantity?: number) => Promise<void>;
  updateQuantity: (lineItemId: string, quantity: number) => Promise<void>;
  removeItem: (lineItemId: string) => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  initializeCart: () => Promise<void>;
}

// Create context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Cart provider component
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Initialize cart on mount
  useEffect(() => {
    initializeCart();
  }, []);

  // Initialize or restore cart
  const initializeCart = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Check for existing cart ID in localStorage
      const existingCartId = localStorage.getItem('shopify-cart-id');
      
      if (existingCartId && isShopifyConfigured()) {
        // Try to fetch existing cart
        try {
          const cart = await getCart(existingCartId);
          dispatch({ type: 'SET_CART_ID', payload: existingCartId });
          dispatch({ type: 'SET_CART_DATA', payload: cart });
          return;
        } catch (error) {
          console.log('Existing cart not found, creating new one');
          localStorage.removeItem('shopify-cart-id');
        }
      }
      
      // Create new cart
      if (isShopifyConfigured()) {
        const cart = await createCart();
        localStorage.setItem('shopify-cart-id', cart.id);
        dispatch({ type: 'SET_CART_ID', payload: cart.id });
        dispatch({ type: 'SET_CART_DATA', payload: cart });
      } else {
        // Use mock cart for development
        const mockCart = mockShopifyResponses.createCart();
        dispatch({ type: 'SET_CART_ID', payload: mockCart.id });
        dispatch({ type: 'SET_CART_DATA', payload: mockCart });
      }
    } catch (error) {
      console.error('Error initializing cart:', error);
      dispatch({ type: 'CART_ERROR', payload: 'Failed to initialize cart' });
    }
  };

  // Add item to cart
  const addToCart = async (product: Product, variant: any, quantity: number = 1) => {
    if (!state.cartId) {
      await initializeCart();
      if (!state.cartId) {
        console.error('❌ Failed to initialize cart');
        return;
      }
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      let cart;
      
      if (isShopifyConfigured()) {
        cart = await shopifyAddToCart(state.cartId, variant.shopifyVariantId, quantity);
      } else {
        // Use mock response for development
        cart = mockShopifyResponses.addToCart(product.name, variant, quantity);
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      dispatch({ type: 'ADD_ITEM_SUCCESS', payload: cart });
      
      // Show success message (you can integrate with a toast system)
      console.log(`✅ Added ${product.name} to cart!`);
      
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      dispatch({ type: 'CART_ERROR', payload: 'Failed to add item to cart' });
      throw error;
    }
  };

  // Update item quantity
  const updateQuantity = async (lineItemId: string, quantity: number) => {
    if (!state.cartId) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      let cart;
      
      if (isShopifyConfigured()) {
        cart = await updateCartItem(state.cartId, lineItemId, quantity);
      } else {
        // Mock update for development
        cart = mockShopifyResponses.updateCartItem(lineItemId, quantity);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      dispatch({ type: 'UPDATE_ITEM_SUCCESS', payload: cart });
    } catch (error) {
      console.error('Error updating cart item:', error);
      dispatch({ type: 'CART_ERROR', payload: 'Failed to update item' });
    }
  };

  // Remove item from cart
  const removeItem = async (lineItemId: string) => {
    if (!state.cartId) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      let cart;
      
      if (isShopifyConfigured()) {
        cart = await removeFromCart(state.cartId, lineItemId);
      } else {
        // Mock removal for development
        cart = mockShopifyResponses.removeFromCart(lineItemId);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      dispatch({ type: 'REMOVE_ITEM_SUCCESS', payload: cart });
    } catch (error) {
      console.error('Error removing cart item:', error);
      dispatch({ type: 'CART_ERROR', payload: 'Failed to remove item' });
    }
  };

  // Open cart drawer
  const openCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: true });
  };

  // Close cart drawer
  const closeCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: false });
  };

  const contextValue: CartContextType = {
    state,
    addToCart,
    updateQuantity,
    removeItem,
    openCart,
    closeCart,
    initializeCart,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 